// src/theme/codeTheme.js
//
// Shared warm-beige palette for code/YAML display used across:
//   - ConfigPreviewPanel.jsx  (inline CSS tokens)
//   - CommandEditor.jsx       (Monaco editor theme)

/** Palette object — use directly as CSS color values. */
export const CODE_PALETTE = {
  bg: "#faf6ef",
  border: "#e5d9c4",
  text: "#3c3530",
  key: "#2e7d4f", // forest green: YAML keys
  string: "#1558a7", // navy blue: string values
  number: "#1558a7", // navy blue: numbers
  bool: "#c05c1a", // burnt orange: true / false / null / ~
  comment: "#9e8a78", // taupe: # comments
  bracket: "#1558a7", // navy blue: [] {}
};

/**
 * Cool slate/white palette for inline code/YAML previews shown inside the
 * signed-in app. Mapped onto the design tokens so the preview sits cleanly in
 * the slate/white UI instead of the warm-beige editor look.
 */
export const CODE_PALETTE_LIGHT = {
  bg: "#F8FAFC", // canvas
  border: "#E2E8F0", // hairline
  text: "#0F172A", // ink
  key: "#2F7A66", // accent-dark teal: YAML keys
  string: "#3B82F6", // blue: string values
  number: "#7C6AED", // violet: numbers
  bool: "#E0922F", // amber: true / false / null / ~
  comment: "#94A3B8", // ink-muted: # comments
  bracket: "#475569", // ink-secondary: [] {}
};

/** Monaco theme identifier */
export const EDITOR_THEME_NAME = "efabric-beige";

/**
 * Light / slate Monaco theme matching the app's design tokens (white surface,
 * slate ink, and brand-adjacent token hues for syntax). Used by the read-only
 * file preview so highlighted code sits cleanly inside the slate/white UI.
 */
export const EDITOR_THEME_LIGHT_NAME = "efabric-light";

export const defineLightEditorTheme = (monaco) => {
  monaco.editor.defineTheme(EDITOR_THEME_LIGHT_NAME, {
    base: "vs",
    inherit: true,
    rules: [
      { token: "", foreground: "0F172A" }, // ink
      { token: "comment", foreground: "94A3B8", fontStyle: "italic" }, // ink-muted
      { token: "keyword", foreground: "2F7A66" }, // accent-dark
      { token: "keyword.operator", foreground: "2F7A66" },
      { token: "string", foreground: "3B82F6" }, // category.cpu (blue)
      { token: "string.escape", foreground: "3B82F6" },
      { token: "number", foreground: "7C6AED" }, // category.ram (violet)
      { token: "constant", foreground: "7C6AED" },
      { token: "constant.language", foreground: "7C6AED" },
      { token: "variable", foreground: "0F172A" },
      { token: "variable.parameter", foreground: "475569" }, // ink-secondary
      { token: "type", foreground: "0F766E" }, // teal
      { token: "support.function", foreground: "2F7A66" },
      { token: "entity.name.function", foreground: "2F7A66" },
      { token: "tag", foreground: "2F7A66" },
      { token: "attribute.name", foreground: "3B82F6" },
      { token: "attribute.value", foreground: "3B82F6" },
      { token: "key", foreground: "0F766E" }, // yaml / json keys
    ],
    colors: {
      "editor.background": "#FFFFFF",
      "editor.foreground": "#0F172A",
      "editor.lineHighlightBackground": "#F1F5F9",
      "editor.lineHighlightBorder": "#00000000",
      "editorLineNumber.foreground": "#CBD5E1",
      "editorLineNumber.activeForeground": "#475569",
      "editor.selectionBackground": "#3F9A8226",
      "editor.inactiveSelectionBackground": "#3F9A8214",
      "editorCursor.foreground": "#0F172A",
      "editorIndentGuide.background": "#E2E8F0",
      "editorIndentGuide.activeBackground": "#CBD5E1",
      "editorGutter.background": "#F8FAFC",
      "scrollbarSlider.background": "#cbd5e166",
      "scrollbarSlider.hoverBackground": "#cbd5e1aa",
      "scrollbarSlider.activeBackground": "#94a3b8aa",
    },
  });
};

/**
 * Registers the beige theme with Monaco. Safe to call multiple times.
 * Pass as the `beforeMount` prop on each @monaco-editor/react Editor.
 */
export const defineEditorTheme = (monaco) => {
  monaco.editor.defineTheme(EDITOR_THEME_NAME, {
    base: "vs",
    inherit: true,
    rules: [
      { token: "", foreground: "3c3530" },
      { token: "comment", foreground: "9e8a78", fontStyle: "italic" },
      { token: "keyword", foreground: "c05c1a" },
      { token: "keyword.operator", foreground: "c05c1a" },
      { token: "string", foreground: "1558a7" },
      { token: "string.escape", foreground: "1558a7" },
      { token: "number", foreground: "1558a7" },
      { token: "constant", foreground: "1558a7" },
      { token: "variable", foreground: "3c3530" },
      { token: "variable.parameter", foreground: "c05c1a" },
      { token: "type", foreground: "2e7d4f" },
      { token: "support.function", foreground: "2e7d4f" },
      { token: "entity.name.function", foreground: "2e7d4f" },
      { token: "tag", foreground: "2e7d4f" },
      { token: "attribute.name", foreground: "1558a7" },
      { token: "attribute.value", foreground: "1558a7" },
    ],
    colors: {
      "editor.background": "#faf6ef",
      "editor.foreground": "#3c3530",
      "editor.lineHighlightBackground": "#f3ede2",
      "editor.selectionBackground": "#e5d9c4",
      "editor.inactiveSelectionBackground": "#ede8e0",
      "editorLineNumber.foreground": "#b0a090",
      "editorLineNumber.activeForeground": "#3c3530",
      "editorCursor.foreground": "#3c3530",
      "editorWhitespace.foreground": "#d5c9b8",
      "editorIndentGuide.background": "#d5c9b8",
      "editorIndentGuide.activeBackground": "#b0a090",
      "scrollbarSlider.background": "#b0a09044",
      "scrollbarSlider.hoverBackground": "#b0a09088",
    },
  });
};
